import { useState, useCallback } from "react";
import { filterObject } from "../helpers";

/**
 * useTableFilter - A simple plug-and-play hook for table filtering
 *
 * @example
 * const filterConfig = [
 *   {
 *     name: "status",
 *     label: t("STATUS"),
 *     type: "select",
 *     options: [
 *       { label: t("ACTIVE"), value: "active" },
 *       { label: t("INACTIVE"), value: "inactive" }
 *     ]
 *   },
 *   {
 *     name: "date_from",
 *     label: t("FROM_DATE"),
 *     type: "input",
 *     inputType: "date"
 *   }
 * ];
 *
 * const { filters, filterFields, handleFilterApply, handleFilterReset } = useTableFilter(filterConfig);
 *
 * <TableC
 *   filterFields={filterFields}
 *   filters={filters}
 *   onFilterApply={handleFilterApply}
 *   onFilterReset={handleFilterReset}
 * />
 *
 * @param {Array} filterConfig - Array of filter field configurations
 * @param {Object} initialFilters - Initial filter values (optional)
 * @returns {Object} Filter state and handlers
 */
export const useTableFilter = (filterConfig = [], initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);

  const handleFilterApply = useCallback((newFilters) => {
    setFilters(() => filterObject(newFilters));
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters({});
  }, []);

  const hasActiveFilters = Object.keys(filters).some(
    (key) =>
      filters[key] !== null && filters[key] !== undefined && filters[key] !== ""
  );

  return {
    filters,
    setFilters, // Direct access for manual updates if needed
    filterFields: filterConfig,
    handleFilterApply,
    handleFilterReset,
    hasActiveFilters,
  };
};
